import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FluentResource } from "@fluent/bundle";
import { LocalizationProvider, Localized } from "@fluent/react";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import LoadableButton from "@kikoff/components/src/v1/buttons/LoadableButton";
import Switch from "@kikoff/components/src/v1/inputs/Switch";
import KLink from "@kikoff/components/src/v1/navigation/KLink";
import useError from "@kikoff/hooks/src/useError";
import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import {
  handleFailedStatus,
  handleProtoStatus,
  protoDate,
  protoTime,
} from "@kikoff/utils/src/proto";
import { combineClasses, format } from "@kikoff/utils/src/string";

import AccountNotFoundCard from "@component/dashboard/account_not_found_card";
import AgreementText, { DocLink } from "@component/legal/agreement_text";
import BottomSheet from "@component/overlay/bottom_sheet";
import {
  findAutopayDefault,
  PaymentMethodSelection,
  PaymentMethodSelectionProps,
} from "@component/payment/payment_method_selection";
import { selectAllowScheduleAutopay } from "@feature/auto_pay";
import { cblCreditCardCutoffDate } from "@feature/cbs";
import { fetchPaymentMethods, selectPaymentMethodBy } from "@feature/funds";
import { dismiss, selectDismissable } from "@feature/page";
import { fetchUser } from "@feature/user";
import { getCreditAccountMentalModelName } from "@src/constants";
import { useOverlay, useOverlaysController } from "@src/overlay";
import analytics, { track } from "@util/analytics";
import { newReactLocalization } from "@util/l10n";
import {
  accountAutoPayEnabled,
  AutoPayAccount,
  autoPayState,
  Payment,
} from "@util/payments";

import styles from "./autopay.module.scss";

export interface AutopayModalProps {
  className?: string;
  account: AutoPayAccount;
  createUpdateSettingsRequest(context: {
    token: string;
    enabled: boolean;
    paymentMethodToken: string;
    dayOfMonth: number;
  }): Promise<any>;

  propsFor?: {
    paymentMethodSelection?: Partial<PaymentMethodSelectionProps>;
  };
  allowScheduling?: boolean;
  highlight?: "scheduling";
  payment: Payment;
  invalidate?(): void;
}

export default function AutopayModal({
  className,
  account,
  createUpdateSettingsRequest,
  propsFor = {},
  allowScheduling,
  highlight,
  payment,
  invalidate,
}: AutopayModalProps) {
  const dispatch = useDispatch();
  const error = useError();
  const overlay = useOverlay(AutopayModal);
  const overlays = useOverlaysController();

  const _enabled = accountAutoPayEnabled(account);
  const enableOnly = !_enabled;

  const [buttonStatus, setButtonStatus] = useState<LoadableButton.Status>(
    "initial"
  );
  const [enabled, setEnabled] = useState(enableOnly || !!_enabled);
  const [paymentMethodToken, setPaymentMethodToken] = useState("");
  const isAch =
    useSelector(selectPaymentMethodBy("token", paymentMethodToken))?.type ===
    web.public_.PaymentType.ACH_BANK_ACCOUNT;
  const [dayOfMonth, setDayOfMonth] = useState(
    account.autoPay.settings.dayOfMonth
  );
  const dayOfMonthChanged = dayOfMonth !== account.autoPay.settings.dayOfMonth;

  const showAchAgreement =
    useSelector(selectDismissable("MUST_ACCEPT_ACH_AGREEMENT")) &&
    isAch &&
    enabled;
  const showTerms = useSelector(selectDismissable("AUTOPAY_TERMS")) && enabled;

  return (
    <BottomSheet
      className={combineClasses(styles["autopay-modal"], className)}
      includeClose
    >
      <header>
        <div className="text:heading-4">
          {enableOnly ? "Enable" : "Manage"} AutoPay
        </div>
        {!enableOnly && (
          <Switch.WithLabel
            checked={enabled}
            onChange={(e) => {
              const autoPayEnabled = e.currentTarget.checked;
              setEnabled(autoPayEnabled);
              track.tap("CA - Payment - Autopay Toggle Button", {
                enabled: autoPayEnabled,
              });
            }}
          />
        )}
      </header>
      <div className={styles["payment-method"]}>
        <header className="text:regular+">Preferred payment method</header>
        <PaymentMethodSelection
          findDefault={findAutopayDefault(account.token)}
          firstIfNoDefault
          {...propsFor.paymentMethodSelection}
          onPaymentMethodTokenChange={(token) => {
            setPaymentMethodToken(token);
            if (
              propsFor.paymentMethodSelection &&
              "onPaymentMethodTokenChange" in propsFor.paymentMethodSelection
            )
              propsFor.paymentMethodSelection?.onPaymentMethodTokenChange?.(
                token
              );
          }}
          paymentOptions={account.paymentOptions}
        />
      </div>
      {allowScheduling && (
        <div
          className={combineClasses(
            styles.schedule,
            highlight === "scheduling" && styles.highlighted
          )}
        >
          <header className="text:regular+">Schedule on</header>
          <ContainerButton
            className={styles.set}
            onClick={async () => {
              const day = await overlays.push(
                "credit_line/change_autopay_date",
                {
                  dayOfMonth,
                }
              );
              if (day != null) {
                setDayOfMonth(day);
                if (highlight === "scheduling")
                  overlay.updateOwnProps({ highlight: null });
              }
            }}
          >
            <div>{format.ordinality(dayOfMonth)} of every month</div>
            <div></div>
          </ContainerButton>
        </div>
      )}
      <div className={`${styles.agreement} text:mini color:moderate`}>
        <AgreementText
          size="mini"
          byClicking={enableOnly ? "Enable AutoPay" : "Save"}
          beforeActions={
            dayOfMonthChanged && "your autopay date will be updated"
          }
        >
          {showTerms && (
            <DocLink href="/autopay-agreement.pdf" type="pdf">
              AutoPay terms
            </DocLink>
          )}
          {showAchAgreement && (
            <DocLink href="/ach-authorization-and-agreement.pdf" type="pdf">
              ACH Authorization Agreement
            </DocLink>
          )}
        </AgreementText>{" "}
        If your preferred payment method can’t be charged, you authorize Kikoff
        to attempt to charge your other payment methods on file.
      </div>
      <KButton
        size="full-width"
        variant="primary"
        state={!enabled || paymentMethodToken ? "active" : "disabled"}
        onClick={async () => {
          if (showTerms) dispatch(dismiss("AUTOPAY_TERMS"));
          if (showAchAgreement) dispatch(dismiss("MUST_ACCEPT_ACH_AGREEMENT"));
          if (!enabled) {
            const disable = await overlays.push(
              "src/components/modals/confirm_disable",
              {}
            );
            if (!disable) {
              if (disable === false) setEnabled(true);
              return;
            }
          }
          setButtonStatus("pending");
          createUpdateSettingsRequest({
            token: account.token,
            enabled,
            paymentMethodToken,
            dayOfMonth,
          })
            .then(
              handleProtoStatus({
                SUCCESS() {
                  setButtonStatus("success");
                  setTimeout(() => {
                    overlay.removeSelf();
                    invalidate?.();
                    dispatch(fetchUser.products());
                    // Update account associations
                    dispatch(fetchPaymentMethods());
                    if (dayOfMonthChanged)
                      overlays.push(
                        "product_accounts/autopay_schedule_confirmation",
                        {
                          dayOfMonth,
                          payment,
                        }
                      );
                    // tracking pixels
                    if (enabled) {
                      analytics.convert("Enable AutoPay");
                    }
                  }, 1500);
                },
                _DEFAULT: handleFailedStatus(
                  "Unable to update AutoPay settings"
                ),
              })
            )
            .catch((e) => {
              setButtonStatus("initial");
              error.throw(e.message);
            });
        }}
        as={LoadableButton}
        status={buttonStatus}
        text={
          enableOnly
            ? {
                pending: "Enabling...",
                success: "Enabled",
              }
            : {
                pending: "Saving...",
                success: "Saved",
              }
        }
      >
        {enableOnly ? "Enable AutoPay" : "Save"}
      </KButton>
    </BottomSheet>
  );
}

const RESOURCES = {
  en: new FluentResource(`autopay-terms = Autopay terms
`),
  es: new FluentResource(`autopay-terms = Términos de pago automático
`),
};

type AutopayTermsLinkProps<T extends KLink.Type = "pdf"> = Partial<
  KLink.Props<T>
>;

export function AutopayTermsLink<T extends KLink.Type = "pdf">(
  props: AutopayTermsLinkProps<T>
) {
  const l10n = newReactLocalization(RESOURCES);

  return (
    <KLink
      href="https://kikoff.com/autopay-agreement.pdf"
      type="pdf"
      variant="primary"
      {...props}
    >
      <LocalizationProvider l10n={l10n}>
        <Localized id="autopay-terms">AutoPay terms</Localized>
      </LocalizationProvider>
    </KLink>
  );
}

// eslint-disable-next-line no-redeclare
export declare namespace AutopayModalProps {
  type Base = Omit<
    AutopayModalProps,
    "paymentTypes" | "account" | "createUpdateSettingsRequest" | "payment"
  >;

  export interface CreditLine extends Base {}

  export interface Loan extends Base {
    type: web.public_.Loan.LoanType;
  }
}

// NOTE: Fast refresh doesn't work on this
AutopayModal.CreditLine = (props: AutopayModalProps.CreditLine) => {
  const dispatch = useDispatch();

  const creditLine = useSelector((state) => state.creditLine.account);
  const allowScheduling = useSelector(selectAllowScheduleAutopay("CA"));

  if (!creditLine) return <AccountNotFoundCard />;

  return (
    <AutopayModal
      className={styles["credit-line"]}
      account={creditLine}
      invalidate={() => {
        dispatch(fetchUser.creditLine());
      }}
      createUpdateSettingsRequest={({
        token,
        enabled,
        paymentMethodToken,
        dayOfMonth,
      }) => {
        if (dayOfMonth !== creditLine.autoPay.settings.dayOfMonth)
          dispatch(dismiss("SCHEDULE_CA_AUTOPAY_PROMPT"));
        return webRPC.CreditLine.updateCreditLineSettings({
          creditLineToken: token,
          autoPay: {
            state: autoPayState(enabled),
            paymentMethodToken,
            dayOfMonth,
          },
        });
      }}
      allowScheduling={allowScheduling}
      payment={{
        label: getCreditAccountMentalModelName(),
        amountCents: creditLine.creditLineAgreement.minimumPaymentAmountCents,
        dueAt: protoTime(creditLine.paymentDueDate),
      }}
      {...props}
    />
  );
};

// NOTE: Fast refresh doesn't work on this
AutopayModal.Loan = ({ type, ...props }: AutopayModalProps.Loan) => {
  const dispatch = useDispatch();

  const loan = useSelector((state) =>
    state.loans.open.find(({ loanType }) => loanType === type)
  );

  const nextInstallment = loan.loanInstallments.find(({ paid }) => !paid);

  if (!loan) return <AccountNotFoundCard />;
  return (
    <AutopayModal
      className={styles["loan"]}
      account={loan}
      invalidate={() => {
        dispatch(fetchUser.loans());
      }}
      createUpdateSettingsRequest={({ token, enabled, paymentMethodToken }) =>
        webRPC.Lending.updateLoanSettings({
          loanToken: token,
          autoPay: {
            state: autoPayState(enabled),
            paymentMethodToken,
          },
        })
      }
      propsFor={{
        paymentMethodSelection: {
          disableMethod(method) {
            const { Status } = web.public_.ExternalBankAccount;

            if (method.type === web.public_.PaymentType.CARD) {
              if (method.status === "frozen" || method.status === "disabled")
                return "Card has been disabled for too many failures. Please visit your accounts page to resolve the issue.";
              if (
                method.stripeDetails?.fundingType ===
                  web.public_.StripeDetails.FundingType.CREDIT &&
                protoDate(method.createdAt) >= cblCreditCardCutoffDate
              ) {
                return "Debit or prepaid cards only for Credit Builder Loan.";
              }
              return false;
            }

            return (
              {
                [Status.PENDING_MANUAL_VERIFICATION]:
                  "You must finish bank verification before AutoPay can be enabled on this account",
                [Status.PENDING_AUTOMATIC_VERIFICATION]:
                  "Your account is still being verified. Please wait for this verification to finish or add another account to enable AutoPay.",
              }[method.externalBankAccount?.status] || false
            );
          },
        },
      }}
      payment={{
        label: "Credit Builder Loan",
        amountCents: nextInstallment.paymentAmountCents,
        dueAt: protoTime(nextInstallment.billingEnd),
      }}
      {...props}
    />
  );
};
